import { Location } from '@angular/common';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthErrorEvent, OAuthStorage } from 'angular-oauth2-oidc';
import jwt_decode from 'jwt-decode';
import {
  BehaviorSubject,
  from,
  Observable,
  of,
  Subject,
  throwError, timer
} from 'rxjs';
import {
  catchError, filter,
  map,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { ConfigurationService } from '@sondermind/configuration';
import { Logger } from '@sondermind/datadog-logging';
import { HttpService } from '@sondermind/http';
import { FlagObservable, FlagSubject } from '@sondermind/models/rxjs';
import { IAuthService } from '@sondermind/utilities/models-auth';
import { SegmentSchema, SegmentService } from '@sondermind/segment';
import { AmplitudeFeatureFlagService } from '@sondermind/amplitude';
import { makeObservableBarrier } from '../barrier';

import { PROJECT_NAME, ProjectAuthNames } from '../project-token';
import { ADMIN_AUTH0_TOKEN_GLOBAL, AUTH0_SIGNUP_PARAM } from '../constants/auth.constants';
import { Auth0TokenService } from './auth0.service';
import { AuthTokenOAuthService } from './auth_token_oauth.service';

export const OAUTH_TOKEN_KEY = 'oauth.access_token';
export const OAUTH_EXPIRATION_KEY = 'oauth.expires_at';

export const LEGACY_JWT_ID_TOKEN_KEY = 'legacy.id_token';
export const LEGACY_JWT_USER_ID_KEY = 'legacy.user_id';
export const LEGACY_JWT_CLIENT_TOKEN_KEY = 'legacy.client_token';
export const LEGACY_IMPERSONATION_KEY = 'legacy.acting_as_member';

export const ADMIN_ACCESS_TOKEN_KEY = 'admin.access_token';
export const ADMIN_PROVIDER_ID_KEY = 'admin.provider_id';

// tokens are considered invalid 15 seconds early,
// so we don't submit a token that's expired by the time it hits the server
export const TOKEN_EXPIRATION_SLOP = 15 * 1000;

/** the JWT data from the legacy token endpoint */
export interface ILegacyJWTData {
  user_id: number;
  client_id: string;
  token: string;
}

/** the HTTP response from the legacy token endpoint */
export interface ILegacyJWTDataResponse {
  token: ILegacyJWTData;
}

/** the fragment from an impersonation request (from the server) */
interface ILegacyImpersonationFragment {
  impersonated: string;
  impersonator: number;
}

interface ITokenInfo {
  sub: string;
  /** JWT expiry, seconds since epoch (present on Auth0/authsvc access tokens). */
  exp?: number;
}

interface IAdminRefreshResponse {
  atoken: string;
  expires_in: number;
}

/**
 * Manages authentication tokens, via OAuth.
 *
 * Based on settings in the `environment`, allows:
 *   * direct login (redirect to login page)
 *   * managing legacy tokens (automatically fetch and sync legacy JWT along with oauth)
 *   * impersonation on login (login with impersonation token from fragment)
 *
 * Token storage is handled in an update/commit fashion, in order to provide atomicity in the
 * case that legacy tokens management is enabled - without it, new requests needing legacy tokens
 * made _during_ a token refresh would be invalid, because the new oauth tokens are setup but the
 * legacy tokens have not been refreshed yet.
 *
 * This means that accessing `OAuthService` from _outside_ this service should be considered a
 * bad smell, since you may end up getting tokens that haven't yet been commited to storage.
 */
@Injectable()
export class AuthTokenService implements IAuthService {
  private initializedFlag = new FlagSubject<boolean>(false);

  /**
   * a flag observable, which can be awaited on, for auth token service initialization.
   * will only be fired once, early in the application cycle
   * emitted value is true once initialization has completed
   */
  initialized$ = this.initializedFlag as FlagObservable<boolean>;

  /**
   * fired anytime an oauth flow has completed.
   * emitted value is true if authenticated after oauth flow, false otherwise
   */
  authFlowComplete$ = new Subject<boolean>();

  /** fired anytime an oauth flow results in a new access token */
  authorized$ = this.authFlowComplete$.pipe(filter((x) => !!x), map(() => true as const));
  /**
   * fired when the token service causes a logout and tokens are destroyed.
   * subscribe to handle things on logout.
  */
  authDestroyed$ = new Subject<void>();
  auth0Token$ = new BehaviorSubject<string>('');
  private adminAccessExpiredSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private configurationService: ConfigurationService,
    private segmentService: SegmentService<SegmentSchema>,
    private amplitudeFeatureFlagService: AmplitudeFeatureFlagService,
    private oauthSvc: AuthTokenOAuthService,
    private oauthStorage: OAuthStorage,
    private http: HttpService,
    private injector: Injector,
    private auth0TokenService: Auth0TokenService,
    private logger: Logger,
    @Inject(PROJECT_NAME) private projectName: ProjectAuthNames,
    private location: Location
  ) {
  }

  /**
   * Checks Auth0 enabled first, if so go auth with auth0 and return success
   *
   * initialize the auth token service;
   * configure oauth, establish the current authentication status, attempt to quietly login.
   * this method will _not_ cause a redirect to login directly.
   *
   * if a client is authenticated during this method, and the last authorization request set
   * a redirect in the oauth state field, this will do that redirect internally.
   *
   * @returns one-shot observable of the current authentication status
   */
  initialize(): Observable<boolean> {
    const isClientAuth0Enabled = localStorage.getItem(AUTH0_SIGNUP_PARAM) === 'true'
      || this.configurationService.env.auth0ClientEnabled;
    const isProviderAuth0Enabled = this.configurationService.env.auth0ProviderEnabled;

    const path = this.location.path(true);
    // All conditions we want to use OLD auth.
    const doingSetup = path.includes('/setup/')
      || path.includes('/signup/')
      || path.includes('/invitation/')
      || path.includes('/onboarding') // during provider onboarding
      || path.includes('#access_token='); // We have an access_token from the old auth flow.
    const isImpersonating = this.isAdminImpersonatingProvider();
    const isAdminAccess = this.isAdminAccessMode() || this.isAdminAccessHashPresent();
    const isClientAndAuth0Enabled = isClientAuth0Enabled && this.projectName === ProjectAuthNames.CLIENT;
    const isProviderAndAuth0Enabled = isProviderAuth0Enabled && this.projectName === ProjectAuthNames.MEMBER;
    const shouldUseAuth0 = !doingSetup
      && !isImpersonating
      && !isAdminAccess
      && (isClientAndAuth0Enabled || isProviderAndAuth0Enabled);

    this.logger.debug(`AuthTokenService initializing - ${shouldUseAuth0 ? 'Auth0' : 'OAuth'}`, {
      doingSetup,
      isImpersonating,
      isAdminAccess,
      isClientAndAuth0Enabled,
      isProviderAndAuth0Enabled
    });

    if (shouldUseAuth0) {
      return this.auth0TokenService.initializeAuth0().pipe(
        tap((token) => {
          this.initializedFlag.next(true);
          if (token) {
            this.auth0Token$.next(token);
            this.auth0TokenService.setAuth0Success();
            this.authFlowComplete$.next(true);
          }
          return token;
        }),
        // Map void to boolean (true means initialized successfully)
        map((token) => !!token),
        catchError((err) => {
          // The typed "Auth0 error:" line from Auth0TokenService is the single source of
          // truth per Auth0 event, so we downgrade this generic duplicate to avoid a second
          // error line with the same dd-request-id. We log at warn (not debug): some init
          // failures reach only this catchError and never transit the SDK's error$ subject
          // (e.g. a jwt_decode throw or a getAccessTokenSilently rejection), and debug is
          // filtered out of prod Datadog — warn keeps a logout-triggering failure visible
          // without inflating the error bucket. The logout side-effect stays.
          this.logger.warn(`auth 0 error: ${err}`);
          this.logger.info(`initiating Auth0 logout`);
          this.auth0TokenService.logout();
          return of(false);
        })
      );
    }

    // ADMIN portal on Auth0: the admin is a server-rendered Rails shell that already
    // ran the Auth0 login and injected the resulting access token onto the page. Prime
    // the OAuth storage from it so the existing interceptor/getAuthToken() path works,
    // WITHOUT an in-browser Auth0 login or an authsvc silent-refresh (there is no authsvc
    // session for an Auth0 admin). Impersonation is unaffected (it uses its own fragment
    // handled below and only ever happens in the member portal).
    if (this.projectName === ProjectAuthNames.ADMIN && !isImpersonating) {
      const injected = this.readInjectedAdminAuth0Token();
      if (injected) {
        this.primeOAuthTokenFromInjected(injected);
        this.initializedFlag.next(true);
        return of(true);
      }
    }

    this.oauthSvc.configure({
      oidc: false,
      loginUrl: `${this.configurationService.env.authBase}/oauth/authorize`,
      clientId: this.configurationService.env.authClientId,
      // authsvc provides the scopes from the server if scopes are blank, but
      // keeps the ability to set it if someone needs to
      scope: '',
      redirectUri: this.configurationService.env.authRedirectUri,

      // in ci environments, we want to not require https, but use the default if not set
      requireHttps: this.configurationService.env.authRequireHttps ?? 'remoteOnly',

      silentRefreshRedirectUri: this.configurationService.env.authSilentRefreshRedirectUri,
      silentRefreshTimeout: 3000, // ms, error if silent refresh hasn't completed in this timeframe
    });

    // reset the live set, so that we don't get cache conflicts
    // (valid oauth, invalid live set; or invalid oauth, but valid legacy, etc)
    // onOAuthComplete commits back to the live set, getting us in sync again
    this.resetOAuthAccessToken();
    this.resetLegacyJWT();

    return this.initializeTryLogin().pipe(
      map(() => this.isAuthenticated()),

      // the login method below stores a redirect url as the oauth state on authorization requests,
      // so if we're logged in, do the redirect
      tap((auth) => {
        if (auth) {
          const { state } = this.oauthSvc;
          if (state && state !== 'undefined' && state !== 'null') {
            this.routeToUrl(state);
          }
        }

        // regardless of login state (no auth token), we're initialized
        this.initializedFlag.next(true);
      })
    );
  }

  /**
   * establish the first login.
   * when this observable completes, the current authentication status is known,
   * and we've tried pretty hard to get valid authentication tokens.
   *
   * @returns one-shot observable, to show completion.
   */
  private initializeTryLogin(): Observable<void> {
    // A fresh launch fragment always wins over a stored token: an admin relaunching the
    // portal in a tab that still holds a stale (often expired) admin token must adopt the
    // new token, otherwise every API call carries the stale one and 401s.
    if (this.isAdminAccessHashPresent()) {
      const parsed = this.parseAdminAccessFragment(this.customHashFragment());
      if (parsed) {
        window.location.hash = '';
        this.storeAdminAccess(parsed.atoken, parsed.ppid);
        this.resetAdminAccessExpired();
        this.startAdminAccessHeartbeat();
        this.authFlowComplete$.next(true);
        // eslint-disable-next-line no-void
        return of(void 0);
      }
    }

    // if admin access tokens are already stored for this session, proceed directly
    if (this.isAdminAccessMode()) {
      this.startAdminAccessHeartbeat();
      this.authFlowComplete$.next(true);
      // eslint-disable-next-line no-void
      return of(void 0);
    }

    // if we have extant tokens already, just use those, and continue on with oauth
    // we don't use `isAuthorized`, because we're still authenticating, and the live set hasn't been updated yet;
    // oAuthComplete will commit tokens, overwriting the live set, so we care that the uncommitted tokens are good.
    if (this.oauthSvc.hasValidAccessToken()) {
      return this.onOAuthComplete();
    }

    const frag = this.customHashFragment();

    // check for impersonation
    if (this.isAdminImpersonatingProvider()) {
      if (this.configurationService.env.authAllowImpersonationLogin) {
        const impersonation = this.parseImpersonationFragment(frag);
        window.location.hash = '';

        this.storeLegacyImpersonating(true);
        return this.loginWithAuthenticationToken(impersonation.impersonated);
      }

      // eslint-disable-next-line no-console
      console.error('auth-token: there is an impersonation fragment, but impersonation is not allowed!');
    }

    // if we're not allowed to authenticate directly, just kick off silent refresh
    if (!this.configurationService.env.authAllowDirectLogin) {
      return this.silentRefresh();
    }

    // PE-9470: preventClearHashAfterLogin true fixes a problem where the angular router sees the hash cleared
    // and schedules a routing event to match, but overrides the routing we do out of authtoken state management,
    // causing state based redirects to not be visible (they happen, but are immediately overridden by a setTimeout
    // in the router :^)

    // try and login based on hash from implicit flow
    // if there was no auth_token in the fragment, attempt silent refresh
    const login = this.oauthSvc.tryLogin({ customHashFragment: frag, preventClearHashAfterLogin: true });
    return from(login).pipe(
      switchMap((auth) => (auth ? this.onOAuthComplete() : this.silentRefresh())),
      catchError((ex) => this.onOAuthError(ex)),
    );
  }

  private parseImpersonationFragment(fragment: string): ILegacyImpersonationFragment {
    const matcher = /^#impersonated=([^&]+)&impersonator=(\d+)$/;
    const result = matcher.exec(fragment);

    return result && {
      impersonated: result[1],
      impersonator: parseInt(result[2], 10)
    };
  }

  /**
   * kicks off the oauth authorization flow.
   *
   * @param returnTo the relative path to redirect to after successful login.
   * @returns nothing - this does not return, but instead forces page navigation.
   */
  login(returnTo: string = null): void {
    this.oauthSvc.initImplicitFlow(returnTo);
  }

  /**
   * login at the server with a one-time password (OTP).
   * OTP may be coming from impersonation header, invite code exchange, etc.
   *
   * Once a successful token authentication request has been made, the browser has the session cookie,
   * and we can force a silent refresh to fully log in.
   *
   * When token-based authentication fails, eventually falls through to login-required.
   */
  loginWithAuthenticationToken(token: string): Observable<void> {
    const url = `${this.configurationService.env.authBase}/session/login/${token}.json`;

    return this.http
      .get(url, new HttpParams({}), { withCredentials: true, skipAuthorization: true })
      .pipe(
        this.http.handleHttpError({ null401: true }),
        switchMap(() => this.silentRefresh())
      );
  }

  /**
   * kills the server-side session, clears token storage, and emits on the destroyed$ event
   *
   * @returns one-shot observable to show completion.
   */
  logout(): Observable<void> {
    if (this.auth0TokenService.auth0Enabled) {
      return this.auth0TokenService.logout();
    }
    // destroy the server session first
    // otherwise, we'll JIT ourselves a new token in order to authenticate the call
    const url = `${this.configurationService.env.authBase}/session/logout.json`;
    return this.http
      .get(url, null, { withCredentials: true, skipAuthorization: true })
      .pipe(
        this.http.handleHttpError({ null401: true }),
        tap(() => {
          this.reset();
          this.oauthSvc.logOut(false);
          // we're logged out at this point, so let folks know
          this.authDestroyed$.next();
        }),
        // eslint-disable-next-line no-void
        map(() => void 0)
      );
  }

  /**
   * clears all client side session state, so that the next request triggers refresh.
   * use this to check for server authentication, without forcing server-side logout.
   */
  reset(): void {
    this.stopAdminAccessHeartbeat();
    this.resetOAuthAccessToken();
    this.resetLegacyJWT();
    this.resetLegacyImpersonating();
    this.resetAdminAccess();
  }

  /**
   * attempt to refresh the current access tokens in the background.
   *
   * uses a hidden iframe to make a prompt=none authorization request, and forwards the code or error via postMessage.
   * This is handled internally by the angular-oauth2-oidc library, we only provide our own to:
   *   * get success/error handling, so we can insert callbacks.
   *   * wrap in an observable barrier, to ensure only one request can pending at a time.
   */
  silentRefresh(): Observable<void> {
    return this.silentRefreshThunk();
  }

  // barrier ensures only one active observable at a time
  private silentRefreshThunk = makeObservableBarrier(() => {
    this.logger.info('silent refresh starting');

    const debounceMs = 100;
    return timer(debounceMs).pipe(
      switchMap(() => from(this.oauthSvc.silentRefresh())),
      switchMap(() => this.onOAuthComplete()),
      catchError((ex) => this.onOAuthError(ex)),
    );
  });

  /**
   * completed oauth flow callback;
   * if authenticated, optionally manage legacy tokens too, and then emit for interested third parties
   *
   * @returns one-show observable, to show completion.
   */
  private onOAuthComplete(): Observable<void> {
    let next: Observable<ILegacyJWTData> = of(null);

    // was oauth successful?
    // (not are we authenticated, because we're still authenticating)
    if (this.oauthSvc.hasValidAccessToken()) {
      if (this.configurationService.env.authManageLegacyToken) {
        next = this.fetchLegacyJWT();
      }
    }

    return next.pipe(
      tap((t) => {
        this.commitAuthTokens(t);
      }),
      catchError((e) => {
        // eslint-disable-next-line no-console
        console.error('error fetching legacy token', e);

        // if we couldn't fetch legacy tokens, something's up with our oauth token
        // or the server is down, or something similar - best we can do is fail the whole thing and try again
        this.reset();
        return of(null);
      }),
      tap((t) => {
        this.logger.info('silent refresh complete', { authenticated: this.isAuthenticated() });
        this.authFlowComplete$.next(this.isAuthenticated());
      }),
      // eslint-disable-next-line no-void
      map(() => void 0)
    );
  }

  /**
   * errored out oauth flow callback;
   * some errors, we try again, others we just announce we're not logged in,
   * others we redirect to error pages, and the rest we rethrow, not knowing how to handle.
   *
   * @param ex the caught exception
   * @returns observable for timing, which may rethrow the exception
   */
  private onOAuthError(ex: any): Observable<void> {
    if (!(ex instanceof OAuthErrorEvent)) {
      return throwError(ex);
    }

    // an error while authorizing should leave us in a known state
    // so reset the tokens and complete the oauth callback with a not-authenticated state
    // eslint-disable-next-line no-void
    const notAuthorizedComplete = of(void 0).pipe(
      tap(() => {
        this.reset();
        this.authFlowComplete$.next(this.isAuthenticated());
      })
    );

    // Recoverable silent refresh (login_required / consent_required -> not-authenticated,
    // handled below) is a normal not-signed-in outcome, not an error: log it at warn.
    const recoverableRefreshError =
      ex.reason?.['params']?.['error'] === 'login_required'
      || ex.reason?.['params']?.['error'] === 'consent_required';
    const isRecoverable = ex.type === 'silent_refresh_error' && recoverableRefreshError;

    this.logger[isRecoverable ? 'warn' : 'error'](`oauth error: ${ex.type}`, { ex });
    this.logger.addError(`oauth error`, { ex });

    switch (ex.type) {
      // this was a response for an outdated request
      // possibly a replay attack? either way, let's start over
      case 'invalid_nonce_in_state':
        return this.silentRefresh();

      // something didn't work at the server
      // likely we weren't signed in to the right persona, or we didn't get authorization
      case 'token_error':
        return from(
          this.routeToUrl(`/error/${ex.params['error']}`)
        ).pipe(
          switchMap(() => notAuthorizedComplete)
        );

      // same as above, but from silent refresh
      // except we might get login_required and consent_required
      case 'silent_refresh_error':

        // if error returned from the server
        if (ex.reason['params']) {
          // not logged in at all, but successful
          if (ex.reason['params']['error'] === 'login_required') {
            return notAuthorizedComplete;
          }

          // need consent, means we're equiv to not logged in, return null
          if (ex.reason['params']['error'] === 'consent_required') {
            return notAuthorizedComplete;
          }
          // otherwise report it
          return from(
            this.routeToUrl(`/error/${ex.reason['params']['error']}`)
          ).pipe(
            switchMap(() => notAuthorizedComplete)
          );
          // check for invalid nonce state in silent refresh error
        // eslint-disable-next-line no-else-return
        } else if (ex.reason['type'] === 'invalid_nonce_in_state') {
          // retry silent refresh
          return this.silentRefresh();
        }

        // route to system error page with the error associated with the silent refresh error
        return from(
          this.routeToUrl(`/error/${ex.reason['type']}`)
        ).pipe(
          switchMap(() => notAuthorizedComplete)
        );

      // if the silent refresh timed out, go to the system error page
      case 'silent_refresh_timeout':
        return from(
          this.routeToUrl('/error/silent_refresh_timeout')
        ).pipe(
          switchMap(() => notAuthorizedComplete)
        );

      // we don't know how to handle other kinds of errors here, just rethrow
      default:
        return throwError(ex);
    }
  }

  /** whether or not we have valid access tokens, and hence can make authenticated requests */
  isAuthenticated(): boolean {
    if (this.isAdminAccessMode()) {
      return true;
    }

    if (!this.hasOAuthAccessToken() && !this.auth0TokenService.getAuth0Success()) {
      return false;
    }

    if (this.configurationService.env.authManageLegacyToken && !this.hasLegacyJWT()) {
      return false;
    }

    return true;
  }

  /** has a valid access token in the live set */
  private hasOAuthAccessToken(): boolean {
    const token = this.getAuthToken();
    const expires = this.getOAuthExpiration() || 0;
    // valid token if the token exists and not expired
    return token && (expires > (new Date().valueOf() + TOKEN_EXPIRATION_SLOP));
  }

  /** get the oauth access token for authentication */
  getAuthToken(): string | null {
    return this.oauthStorage.getItem(OAUTH_TOKEN_KEY);
  }

  /**
   * Read the Auth0 access token injected by the Rails admin shell (server-side-token
   * model). Returns null when absent (e.g. legacy authsvc admin session).
   */
  private readInjectedAdminAuth0Token(): string | null {
    const token = (window as unknown as Record<string, unknown>)[ADMIN_AUTH0_TOKEN_GLOBAL];
    return typeof token === 'string' && token.length > 0 ? token : null;
  }

  /**
   * Store an injected Auth0 access token in the OAuth live set so the shared interceptor
   * and getAuthToken() use it. Expiration is read from the JWT `exp` claim; if it can't be
   * parsed we fall back to a short window so the widget will re-read the page's fresh token
   * on next load rather than trusting a stale one.
   */
  private primeOAuthTokenFromInjected(token: string): void {
    this.oauthStorage.setItem(OAUTH_TOKEN_KEY, token);
    let expiresAtMs: number;
    try {
      const decoded = jwt_decode<ITokenInfo>(token);
      expiresAtMs = decoded?.exp ? decoded.exp * 1000 : Date.now();
    } catch {
      expiresAtMs = Date.now();
    }
    this.oauthStorage.setItem(OAUTH_EXPIRATION_KEY, String(expiresAtMs));
  }

  /**
   * Returns the current access token as an observable, supporting both Auth0 and legacy OAuth.
   * Use this when the token may be stored in either format (e.g. SSO redirects, external iframes).
   */
  getAccessToken$(): Observable<string | null> {
    if (!this.auth0TokenService.auth0Enabled) {
      return of(this.isAuthenticated() ? this.getAuthToken() : null);
    }
    if (!this.isAuthenticated()) {
      return of(null);
    }
    return this.auth0TokenService.getTokenWithBarrier().pipe(
      take(1),
      map((token) => token || null),
      catchError(() => of(null))
    );
  }

  /** get the oauth access token for authentication */
  getAuthPlatformUuid(): string | null {
    const token = this.oauthStorage.getItem(OAUTH_TOKEN_KEY);
    const decoded: ITokenInfo = jwt_decode(token);
    return decoded?.sub || '';
  }

  /** get the oauth access token expiration time */
  getOAuthExpiration(): number | null {
    const val = this.oauthStorage.getItem(OAUTH_EXPIRATION_KEY);
    return val && +val;
  }

  /** has a valid legacy jwt in the live set */
  private hasLegacyJWT(): boolean {
    return this.hasOAuthAccessToken() && !!this.getLegacyJWT();
  }

  /**
   * get the legacy JWT token for authentication.
   * prints a console error if this is requested but legacy auth is disabled
   */
  private getLegacyJWT(): string {
    if (!this.configurationService.env.authManageLegacyToken) {
      // eslint-disable-next-line no-console
      console.error('auth-token: requesting legacy token, but legacy tokens are not enabled!');
    }

    return this.oauthStorage.getItem(LEGACY_JWT_ID_TOKEN_KEY);
  }

  /** whether the current session is using an admin access token instead of OAuth/Auth0 */
  isAdminAccessMode(): boolean {
    return !!sessionStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
  }

  /** get the admin access token stored in session storage */
  getAdminAccessToken(): string | null {
    return sessionStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
  }

  /** get the provider id associated with the current admin access session */
  getAdminProviderId(): string | null {
    return sessionStorage.getItem(ADMIN_PROVIDER_ID_KEY);
  }

  /** emits true once the admin-access session can no longer be refreshed */
  adminAccessSessionExpired$ = this.adminAccessExpiredSubject.asObservable();

  /** true once the admin-access session has entered the terminal (unrefreshable) state */
  get isAdminAccessExpired(): boolean {
    return this.adminAccessExpiredSubject.value;
  }

  /** decode the stored admin access token's expiry, in ms since epoch */
  getAdminAccessTokenExpiryMs(): number | null {
    const token = this.getAdminAccessToken();
    if (!token) {
      return null;
    }
    try {
      const decoded = jwt_decode<ITokenInfo>(token);
      return decoded?.exp ? decoded.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  /** true if the admin token is missing or within `marginMs` of expiry */
  isAdminAccessTokenExpiringWithin(marginMs: number): boolean {
    const expiryMs = this.getAdminAccessTokenExpiryMs();
    if (expiryMs === null) {
      return true;
    }
    return expiryMs - new Date().valueOf() <= marginMs;
  }

  /** enter the terminal "session expired — relaunch" state */
  markAdminAccessExpired(): void {
    this.adminAccessExpiredSubject.next(true);
  }

  /** leave the terminal state, e.g. when a fresh admin token is launched into the portal */
  private resetAdminAccessExpired(): void {
    if (this.adminAccessExpiredSubject.value) {
      this.adminAccessExpiredSubject.next(false);
    }
  }

  /** silently refresh the admin access token via the cookie-less API endpoint */
  refreshAdminAccessToken(): Observable<string> {
    return this.refreshAdminAccessThunk();
  }

  private refreshAdminAccessThunk = makeObservableBarrier<string>(() => {
    const url = `${this.configurationService.env.apiBase}/admin_access/refresh_token.json`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.getAdminAccessToken()}`,
      'X-Provider-Id': this.getAdminProviderId() || ''
    });
    // explicit headers => the auth interceptor's admin branch is bypassed (no recursion)
    return this.http.post<IAdminRefreshResponse>(url, {}, new HttpParams(), { headers }).pipe(
      tap((res) => {
        sessionStorage.setItem(ADMIN_ACCESS_TOKEN_KEY, res.atoken);
      }),
      map((res) => res.atoken),
      catchError((ex) => {
        this.logger.info('admin access refresh failed; entering terminal state');
        this.markAdminAccessExpired();
        return throwError(() => ex);
      })
    );
  });

  private adminHeartbeatTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private readonly adminHeartbeatLeadMs = 60 * 1000;
  private readonly adminHeartbeatFloorMs = 30 * 1000;

  /** proactively refresh the admin token shortly before it expires */
  startAdminAccessHeartbeat(): void {
    this.stopAdminAccessHeartbeat();
    const expiryMs = this.getAdminAccessTokenExpiryMs();
    if (expiryMs === null) {
      return;
    }
    const delay = Math.max(
      expiryMs - new Date().valueOf() - this.adminHeartbeatLeadMs,
      this.adminHeartbeatFloorMs
    );
    this.adminHeartbeatTimeoutId = setTimeout(() => {
      this.refreshAdminAccessToken().pipe(
        catchError(() => of(null))
      ).subscribe((token) => {
        if (token) {
          this.startAdminAccessHeartbeat();
        }
      });
    }, delay);
  }

  stopAdminAccessHeartbeat(): void {
    if (this.adminHeartbeatTimeoutId !== null) {
      clearTimeout(this.adminHeartbeatTimeoutId);
      this.adminHeartbeatTimeoutId = null;
    }
  }

  /**
   * get the legacy impersonation state.
   * prints a console error if this is requested but legacy auth is disabled.
   */
  isLegacyImpersonating(): boolean {
    if (!this.configurationService.env.authAllowImpersonationLogin) {
      // eslint-disable-next-line no-console
      console.error('auth-token: requesting impersonation bool, but impersonation is not enabled!');
    }

    return !!this.oauthStorage.getItem(LEGACY_IMPERSONATION_KEY);
  }

  isIdleCheckEnabled(): boolean {
    return true;
  }

  /** clear oauth access token from storage. */
  private resetOAuthAccessToken() {
    this.oauthStorage.removeItem(OAUTH_TOKEN_KEY);
    this.oauthStorage.removeItem(OAUTH_EXPIRATION_KEY);
  }

  /** clear the legacy token info from storage */
  private resetLegacyJWT(): void {
    this.oauthStorage.removeItem(LEGACY_JWT_ID_TOKEN_KEY);
    this.oauthStorage.removeItem(LEGACY_JWT_CLIENT_TOKEN_KEY);
    this.oauthStorage.removeItem(LEGACY_JWT_USER_ID_KEY);
  }

  /**
   * fetches a legacy JWT from the api server.
   * despite looking like a v1 request, this is an oauth token based request.
   *
   * server-side, this generates a new legacy JWT with the same expiration data
   * as the currently authenticated oauth token.
   */
  private fetchLegacyJWT(): Observable<ILegacyJWTData> {
    const url = `${this.configurationService.env.apiBase}/sessions/legacy_token.json`;
    const headers = new HttpHeaders({
      Authorization: this.oauthSvc.authorizationHeader()
    });

    // we provide our own header, in order to skip the auth interceptor
    // because this runs _before_ we've commited the new tokens

    return this.http
      .get<ILegacyJWTDataResponse>(url, null, { headers })
      .pipe(
        map((res) => res.token),
        this.http.handleHttpError(),
      );
  }

  /** update the live set with token data */
  private commitAuthTokens(token?: ILegacyJWTData): void {
    if (token) {
      // move the new legacy tokens into the live set
      this.oauthStorage.setItem(LEGACY_JWT_ID_TOKEN_KEY, token.token);
      this.oauthStorage.setItem(LEGACY_JWT_CLIENT_TOKEN_KEY, token.client_id);
      this.oauthStorage.setItem(LEGACY_JWT_USER_ID_KEY, token.user_id.toString());
    }

    // move the oauth service values into the live set
    const accessToken = this.oauthSvc.getAccessToken();
    this.oauthStorage.setItem(OAUTH_TOKEN_KEY, accessToken);
    this.oauthStorage.setItem(OAUTH_EXPIRATION_KEY, this.oauthSvc.getAccessTokenExpiration().toString());
    const decoded: ITokenInfo = jwt_decode(accessToken);
    this.segmentService.identifyUser(decoded.sub);
    this.amplitudeFeatureFlagService.setUserId(decoded.sub);
  }

  /** store the legacy impersonation info into the same storage the oauth system users (session probably) */
  private storeLegacyImpersonating(val: boolean): void {
    if (val) {
      this.oauthStorage.setItem(LEGACY_IMPERSONATION_KEY, 'true');
    } else {
      this.oauthStorage.removeItem(LEGACY_IMPERSONATION_KEY);
    }
  }

  /** clear the legacy impersonation info out of storage */
  private resetLegacyImpersonating(): void {
    this.oauthStorage.removeItem(LEGACY_IMPERSONATION_KEY);
  }

  /**
   * fetch the hash fragment to use during implicit authorization flow.
   *
   * for some reason, errors come in a query string from doorkeeper
   * if the hash is empty, and there's a query string, use the query string as the custom hash
   */
  private customHashFragment(): string {
    let fragment: string;
    // eslint-disable-next-line no-restricted-globals
    fragment = location.hash.length > 0 ? location.hash : location.search;
    fragment = `#${fragment.slice(1)}`;

    return fragment;
  }

  private isAdminImpersonatingProvider(): boolean {
    const fragment = this.customHashFragment();
    return fragment.startsWith('#impersonated')
      && (document.referrer.includes('admin.sondermind')
        || document.referrer.includes('localhost:3000'));
  }

  /**
   * whether the URL hash contains an admin access token and provider id.
   * We key only on the fragment: the atoken is a server-verified JWT (replayed as
   * X-Provider-Id, which auth-service re-verifies), so document.referrer is not a trust
   * boundary — and it is frequently empty (Referrer-Policy: no-referrer, cross-origin
   * redirects), which would otherwise drop the fresh token and 401 every admin API call.
   */
  private isAdminAccessHashPresent(): boolean {
    return this.customHashFragment().startsWith('#atoken=');
  }

  /** parse admin access token and provider id from the URL hash fragment */
  private parseAdminAccessFragment(fragment: string): { atoken: string; ppid: string } | null {
    const matcher = /^#atoken=([^&]+)&ppid=([^&]+)$/;
    const result = matcher.exec(fragment);
    return result ? { atoken: result[1], ppid: result[2] } : null;
  }

  /** persist admin access token and provider id to session storage */
  private storeAdminAccess(atoken: string, ppid: string): void {
    sessionStorage.setItem(ADMIN_ACCESS_TOKEN_KEY, atoken);
    sessionStorage.setItem(ADMIN_PROVIDER_ID_KEY, ppid);
  }

  /** remove admin access token and provider id from session storage */
  private resetAdminAccess(): void {
    sessionStorage.removeItem(ADMIN_ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(ADMIN_PROVIDER_ID_KEY);
  }

  /**
   * route to the given URL
   * @param url the url to route to.
   */
  private routeToUrl(url: string): Promise<boolean> {
    // pull the router from injector; if it's in the constructor params,
    // we get a circular dependency with AppErrorHandler for some reason
    if (this.configurationService.env.authAllowDirectLogin) {
      return this.injector.get(Router).navigateByUrl(url);
    }

    // if we're not allowing direct login, that means we don't control the url, and can't route
    return Promise.resolve(false);
  }
}
